import React, { useEffect, useMemo, useRef } from 'react';
import { TableColumn, TableRow } from './types';
import { getStickyOffsets } from './utils';
import useCellWidthCalculation from './useCellWidthCalculation';
import { ContentContainer, RowContainer, Cell, StickyCell } from './ContentStyles';
import LoadingMoreIndicator from './LoadingMoreIndicator';

interface Props {
  columns: TableColumn[];
  expandedColumns: Set<string>;
  rows: TableRow[];
  identifier?: string;
  scrollRef?: (el: HTMLDivElement | null) => void;
  maxHeight?: string;
  isLoadingMore?: boolean;
  handleFetchMoreData?: () => void;
}

interface LeafColumn {
  key: string;
  render?: (row: TableRow, columnKey: string, identifier: string) => React.ReactNode;
  backgroundColor?: (row: TableRow, columnKey: string, identifier: string) => string;
  stickyLeft?: number;
  collapsed?: boolean;
}

const getLeafColumns = (
  columns: TableColumn[],
  expandedColumns: Set<string>,
  stickyOffsets: Map<string, number>,
): LeafColumn[] => {
  const leaves: LeafColumn[] = [];
  for (const column of columns) {
    const stickyLeft = stickyOffsets.get(column.key);
    if (!column.children?.length) {
      leaves.push({
        key: column.key,
        render: column.render,
        backgroundColor: column.backgroundColor,
        stickyLeft,
      });
    } else if (expandedColumns.has(column.key)) {
      for (const child of column.children) {
        leaves.push({
          key: child.key,
          render: child.render,
          backgroundColor: child.backgroundColor,
        });
      }
    } else {
      const lastChild = column.children[column.children.length - 1];
      leaves.push({
        key: lastChild.key,
        render: lastChild.render,
        backgroundColor: lastChild.backgroundColor,
        collapsed: true,
      });
    }
  }
  return leaves;
};

const getCellValue = (
  row: TableRow,
  key: string,
  columns: TableColumn[],
  expandedColumns: Set<string>,
): unknown => {
  if (key in row.rowDetails) {
    return row.rowDetails[key] ?? row.label;
  }

  const parentColumn = columns.find(
    (col) =>
      col.children?.length &&
      !expandedColumns.has(col.key) &&
      col.children[col.children.length - 1].key === key,
  );
  if (parentColumn) {
    return row.lastColumnDetails[parentColumn.key] ?? '-';
  }

  return row.data[key] ?? '-';
};

const Content = ({
  columns,
  expandedColumns,
  rows,
  identifier = 'default',
  scrollRef,
  maxHeight,
  isLoadingMore,
  handleFetchMoreData,
}: Props) => {
  const { gridTemplateColumns, minWidth } = useCellWidthCalculation(columns, expandedColumns);
  const stickyOffsets = useMemo(() => getStickyOffsets(columns), [columns]);
  const leafColumns = getLeafColumns(columns, expandedColumns, stickyOffsets);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const setScrollRef = (el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    scrollRef?.(el);
  };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root || !handleFetchMoreData) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore) {
          handleFetchMoreData();
        }
      },
      { root, threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleFetchMoreData]);

  return (
    <ContentContainer ref={setScrollRef} sx={{ ...(maxHeight && { maxHeight }) }}>
      {rows.map((row, index) => (
        <RowContainer key={index} sx={{ gridTemplateColumns, minWidth: `${minWidth}px` }}>
          {leafColumns.map((leaf) => {
            const bgColor = leaf.collapsed
              ? '#F0F0F1'
              : leaf.backgroundColor?.(row, leaf.key, identifier);
            const isSticky = leaf.stickyLeft !== undefined;
            const CellComponent = isSticky ? StickyCell : Cell;

            return (
              <CellComponent
                key={leaf.key}
                sx={{
                  ...(bgColor && { backgroundColor: bgColor }),
                  ...(isSticky && { left: leaf.stickyLeft }),
                }}
              >
                {leaf.render
                  ? leaf.render(row, leaf.key, identifier)
                  : String(getCellValue(row, leaf.key, columns, expandedColumns))}
              </CellComponent>
            );
          })}
        </RowContainer>
      ))}
      {handleFetchMoreData && <div ref={sentinelRef} style={{ height: 1 }} />}
      {isLoadingMore && <LoadingMoreIndicator />}
    </ContentContainer>
  );
};

export default Content;
